import { useSyncExternalStore } from 'react'
import { ITaskProjectFavorite } from '@common/interfaces/taskProject'
import { ROUTES } from '../constants/routes'
import { isRememberedTaskRoute } from '../helpers/taskTabs'

// The last Tasks tab this browser opened (My Tasks or a favorited project), so the
// sidebar's Tasks item returns the user to where they work. localStorage rather than the user's
// `displaySettings`: this is view state like the density and column-width
// preferences, and it costs no request per tab switch.
//
// Behind useSyncExternalStore for the same reason useDensityPreference is — the
// component that writes it (TaskTabs) and the one that reads it (SideMenu) are
// in different trees. Reading localStorage directly inside SideMenu's render
// left the link frozen at whatever it was on first paint, since nothing in
// React's dependency graph changes when a tab switch writes the key.
const STORAGE_KEY = 'atlas.tasks.lastTab'

const listeners = new Set<() => void>()
// Doubles as the snapshot cache: getSnapshot must return a referentially stable
// value between writes or useSyncExternalStore re-renders forever.
let cached: string | null | undefined

const readStoredValue = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // localStorage unavailable (private mode / disabled) — always My Tasks.
    return null
  }
}

const getSnapshot = (): string | null => {
  if (cached === undefined) {
    cached = readStoredValue()
  }
  return cached
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const rememberTaskTab = (pathname: string): void => {
  if (getSnapshot() === pathname) {
    return
  }
  cached = pathname
  try {
    window.localStorage.setItem(STORAGE_KEY, pathname)
  } catch {
    // Persisting is best-effort — the in-memory value still steers this session.
  }
  listeners.forEach((listener) => listener())
}

/**
 * Where the sidebar's Tasks item should point: the last of My Tasks or a favorited
 * project the user opened, else My Tasks.
 *
 * Re-validated against the live favorites on every read — a remembered project
 * that was since unfavorited is merely stale, but one that was deleted would 404
 * from ShowProject, and any ID_NOT_FOUND hard-redirects the whole app to
 * "Page not found".
 */
export const useLastTaskTab = (favorites: ITaskProjectFavorite[]): string => {
  const remembered = useSyncExternalStore(subscribe, getSnapshot, (): string | null => null)
  return remembered && isRememberedTaskRoute(remembered, favorites) ? remembered : ROUTES.MY_TASKS
}
